import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, ConfirmationDialog, Loader } from 'src/components-dummy';
import {
  experimentService,
  ApplyPreviewResult,
  DriftResolutionChoice,
  Experiment,
  ExperimentSlim,
} from 'src/services';
import { ApplyDriftSection } from './ApplyDriftSection';
import {
  buildDriftResolutions,
  driftKey,
  formatApplySummary,
  formatDomainLabel,
  formatFieldValue,
  formatReplacesWithoutDriftCheck,
} from './applyWinnerModal.utils';
import './ApplyWinnerModal.scss';

export interface ApplyWinnerModalProps {
  shopId: number;
  experimentId: string;
  winningVariantId: string;
  onApplied: (experiment: Experiment | ExperimentSlim) => void;
  onCancel: () => void;
}

export const ApplyWinnerModal = ({
  shopId,
  experimentId,
  winningVariantId,
  onApplied,
  onCancel,
}: ApplyWinnerModalProps): JSX.Element => {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const [preview, setPreview] = useState<ApplyPreviewResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [driftChoice, setDriftChoice] = useState<DriftResolutionChoice>('keep-live');
  const [reloadCount, setReloadCount] = useState(0);
  const [applyError, setApplyError] = useState<string | null>(null);

  useEffect(() => {
    cancelButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    experimentService
      .getApplyPreview({ shopId, experimentId, winningVariantId })
      .then(result => {
        if (cancelled) return;
        setPreview(result);
        setDriftChoice('keep-live');
      })
      .catch(() => {
        if (cancelled) return;
        setError('Failed to load the apply preview. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [shopId, experimentId, winningVariantId, reloadCount]);

  const summary = useMemo(
    () =>
      preview
        ? formatApplySummary({
            writeCount: preview.toWrite.length,
            driftCount: preview.drifted.length,
            choice: driftChoice,
          })
        : null,
    [preview, driftChoice]
  );

  const replacesWarning = preview
    ? formatReplacesWithoutDriftCheck(preview.replacesWithoutDriftCheck, preview.winningVariantName)
    : null;

  const onConfirm = async (): Promise<void> => {
    if (applying) return;
    if (!preview) return;
    setApplying(true);
    setApplyError(null);
    try {
      const driftResolutions = buildDriftResolutions(preview.drifted, driftChoice);
      const updatedExperiment = await experimentService.applyWinningVariant({
        shopId,
        experimentId,
        winningVariantId,
        driftResolutions,
      });
      onApplied(updatedExperiment);
    } catch (e) {
      const { code, message } = (e ?? {}) as { code?: number; message?: unknown };
      const isClientError = typeof code === 'number' && code >= 400 && code < 500;
      setApplyError(
        isClientError && typeof message === 'string' && message
          ? message
          : 'Failed to apply the winning variant. Please try again.'
      );
      setApplying(false);
      setReloadCount(count => count + 1);
    }
  };

  const handleCancel = (): void => {
    if (applying) return;
    onCancel();
  };

  return (
    <ConfirmationDialog
      onCancel={handleCancel}
      disableAutoFocus
      className='apply-winner-modal'
      sx={{ '& .MuiDialog-paper': { maxWidth: 760 } }}
    >
      <ConfirmationDialog.Header>
        <ConfirmationDialog.Title>
          {preview ? `Apply ${preview.winningVariantName} to your shop?` : 'Apply winner to shop?'}
        </ConfirmationDialog.Title>
        <ConfirmationDialog.HeaderCloseButtonWithIcon onCancel={handleCancel} />
      </ConfirmationDialog.Header>
      <ConfirmationDialog.Content>
        <ConfirmationDialog.ContentMainText className='apply-winner-modal-description'>
          This saves the winning config as pending changes that you review and publish, and ends the
          experiment. Traffic stops splitting within about 5 minutes.
        </ConfirmationDialog.ContentMainText>
        <div className='apply-winner-modal-divider' />

        {loading && (
          <div className='apply-winner-modal-loading'>
            <Loader />
          </div>
        )}

        {error && (
          <div className='apply-winner-modal-error'>
            {error}
            <Button variant='outlined' onClick={() => setReloadCount(count => count + 1)}>
              Try again
            </Button>
          </div>
        )}

        {applyError && <div className='apply-winner-modal-error'>{applyError}</div>}

        {!loading && preview && (
          <>
            <div className='apply-winner-modal-section'>
              <h4>What will be applied</h4>
              {preview.toWrite.length === 0 ? (
                <p className='apply-winner-modal-empty'>No config fields need to change.</p>
              ) : (
                <ul className='apply-winner-modal-write-list'>
                  {preview.toWrite.map(write => (
                    <li key={driftKey(write)}>
                      <span className='field-domain'>{formatDomainLabel(write.domain)}</span>
                      <span className='field-name'>{write.valuePath}</span>
                      <span className='field-old-value'>{formatFieldValue(write.oldValue)}</span>
                      <span className='field-arrow'>→</span>
                      <span className='field-new-value'>{formatFieldValue(write.newValue)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {replacesWarning && <p className='apply-winner-modal-footnote'>{replacesWarning}</p>}
              <p className='apply-winner-modal-footnote'>Nothing else on the shop is touched.</p>
            </div>

            {preview.drifted.length > 0 && (
              <ApplyDriftSection
                drifted={preview.drifted}
                winningVariantName={preview.winningVariantName}
                choice={driftChoice}
                disabled={applying}
                onChoiceChange={setDriftChoice}
              />
            )}

            {preview.skipped.length > 0 && (
              <div className='apply-winner-modal-section'>
                <h4>Not applied</h4>
                <ul className='apply-winner-modal-skip-list'>
                  {preview.skipped.map(skip => (
                    <li key={driftKey(skip)}>
                      <span className='field-name'>{skip.valuePath}</span> — {skip.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </ConfirmationDialog.Content>
      <ConfirmationDialog.Footer className='apply-winner-modal-footer'>
        <div className='apply-winner-modal-footer-row'>
          <div className='apply-winner-modal-buttons'>
            <Button
              variant='primary'
              disabled={loading || !preview || applying}
              loading={applying}
              onClick={onConfirm}
            >
              Apply and end test
            </Button>
            <Button ref={cancelButtonRef} variant='outlined' onClick={onCancel} disabled={applying}>
              Cancel
            </Button>
          </div>
          {summary && <p className='apply-winner-modal-summary'>{summary}</p>}
        </div>
      </ConfirmationDialog.Footer>
    </ConfirmationDialog>
  );
};
